// Table Predictions – rebuilt minimal but functional multi‑league page
import React, { useEffect, useMemo, useState, useContext } from "react";
import API from "../api";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/ui/Card";
import CompetitionFilterBar from "../components/CompetitionFilterBar";
import { usePageTitle } from "../hooks/usePageTitle";
import { getTeamLogo, getTeamInitials } from "../getTeamLogo";
import { UserContext } from "../context/UserContext";
import {
  filterCompetitionsByEnabled,
  getEnabledCompetitionIds,
  pickInitialCompetitionId,
} from "../constants/competitionPrefs";
import {
  CURRENT_SEASON,
  SEASON_OPTIONS,
  formatSeasonLabel,
  shouldShowTablePredictionsOpenBanner,
} from "../constants/season";

import SeasonSelect from "../components/SeasonSelect";

const sortSeasonIdsDesc = (ids) =>
  [...ids].sort((a, b) => String(b).localeCompare(String(a)));

const normalizeCrestKey = (name) =>
  (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const addCrestToMap = (map, teamName, crestUrl) => {
  if (!teamName || !crestUrl) return;
  const variants = new Set([
    teamName,
    teamName.replace(/^(FC|CF|AFC|SC|RCD|RC|CA|CD|UD)\s+/i, ""),
    teamName.replace(/\s+(FC|CF|AFC|SC)$/i, ""),
  ]);
  variants.forEach((name) => {
    const key = normalizeCrestKey(name);
    if (key) map[key] = crestUrl;
  });
};

const resolveTeamLogo = (teamName, logoDir, crestMap = {}) =>
  crestMap[normalizeCrestKey(teamName)] || getTeamLogo(teamName, logoDir);

const TeamLogo = ({ teamName, logoDir, crestMap, className = "w-8 h-8" }) => {
  const [failed, setFailed] = useState(false);
  const src = resolveTeamLogo(teamName, logoDir, crestMap);

  if (!src || failed) {
    return (
      <div
        className={`${className} flex items-center justify-center text-[10px] font-bold text-gray-500 border border-gray-200 rounded bg-gray-50 flex-shrink-0`}
      >
        {getTeamInitials(teamName)}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={teamName}
      className={`${className} object-contain flex-shrink-0`}
      onError={() => setFailed(true)}
    />
  );
};

const TABLE_COMPETITIONS = [
  { id: "premier_league", label: "Premier League", logoDir: "england__premier_league", teamCount: 20 },
  { id: "laliga", label: "LaLiga", logoDir: "spain__laliga", teamCount: 20 },
  { id: "bundesliga", label: "Bundesliga", logoDir: "germany__bundesliga", teamCount: 18 },
  { id: "serie_a", label: "Serie A", logoDir: "italy__serie_a", teamCount: 20 },
];

const getTableTeamCount = (competition) =>
  TABLE_COMPETITIONS.find((c) => c.id === competition)?.teamCount || 20;

const DEFAULT_TEAMS = {
  premier_league: [
    "Arsenal","Aston Villa","Bournemouth","Brentford","Brighton Hove","Chelsea","Coventry City","Crystal Palace","Everton","Fulham","Hull City","Ipswich Town","Leeds United","Liverpool","Manchester City","Manchester United","Newcastle","Nottingham Forest","Sunderland","Tottenham"
  ],
  laliga: [
    "FC Barcelona","Real Madrid CF","Club Atlético de Madrid","Villarreal CF","Real Betis Balompié","RCD Espanyol de Barcelona","RC Celta de Vigo","Real Sociedad de Fútbol","CA Osasuna","Deportivo Alavés","Athletic Club","Girona FC","Elche CF","Sevilla FC","RCD Mallorca","Valencia CF","Getafe CF","Rayo Vallecano de Madrid","Levante UD","Real Oviedo"
  ],
  bundesliga: [
    "Bayern","VfB Stuttgart","RB Leipzig","Mönchengladbach","Mainz","Paderborn","Köln","Hoffenheim","Union Berlin","Eintracht Frankfurt","SV Elversberg","Leverkusen","Dortmund","Hamburg","Freiburg","Werder","Augsburg","Schalke"
  ],
  serie_a: [
    "Inter","Juventus","Milan","Napoli","Roma","Lazio","Atalanta","Fiorentina","Torino","Bologna","Genoa","Cagliari","Sassuolo","Udinese","Lecce","Verona","Como","Parma","Pisa","Cremonese"
  ],
};
const buildDefaultTable = (comp) =>
  (DEFAULT_TEAMS[comp] || DEFAULT_TEAMS.premier_league).map((team, idx) => ({
    position: idx + 1,
    team_name: team,
  }));

const normalizeTableKey = (name = "") =>
  String(name)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");

const tableKeysAlign = (a, b) => {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.includes(b) || b.includes(a);
};

const predictionsMatchStandings = (predictions, standings) => {
  if (!Array.isArray(predictions) || !Array.isArray(standings)) return false;
  if (!predictions.length || predictions.length !== standings.length) return false;

  const predsMatchStandings = predictions.every((pred) => {
    const predKey = normalizeTableKey(pred.team_name);
    return standings.some((row) =>
      tableKeysAlign(predKey, normalizeTableKey(row.team_name))
    );
  });

  const standingsMatchPreds = standings.every((row) => {
    const rowKey = normalizeTableKey(row.team_name);
    return predictions.some((pred) =>
      tableKeysAlign(rowKey, normalizeTableKey(pred.team_name))
    );
  });

  return predsMatchStandings && standingsMatchPreds;
};

const ensureSequentialPositions = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const positions = rows.map((row) => Number(row.position));
  const needsRenumber =
    positions.some((pos) => !Number.isFinite(pos) || pos < 1) ||
    new Set(positions).size !== rows.length;
  if (!needsRenumber) return rows;
  return rows.map((row, idx) => ({ ...row, position: idx + 1 }));
};

const buildTableFromStandings = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return ensureSequentialPositions(
    rows.map(({ position, team_name }) => ({
      position: Number(position),
      team_name,
    }))
  );
};

const resolvePredictedTable = ({ savedPredictions, standingsRows, competition, isPast }) => {
  const fromStandings = buildTableFromStandings(standingsRows);

  if (fromStandings) {
    if (
      !isPast &&
      Array.isArray(savedPredictions) &&
      savedPredictions.length > 0 &&
      predictionsMatchStandings(savedPredictions, fromStandings)
    ) {
      return savedPredictions.map((pred) => {
        const match = fromStandings.find((row) =>
          tableKeysAlign(
            normalizeTableKey(pred.team_name),
            normalizeTableKey(row.team_name)
          )
        );
        return {
          position: pred.position,
          team_name: match?.team_name || pred.team_name,
        };
      });
    }
    return fromStandings;
  }

  if (isPast && Array.isArray(savedPredictions) && savedPredictions.length > 0) {
    return savedPredictions;
  }
  if (isPast) return [];
  return buildDefaultTable(competition);
};

// ─── Helper function to determine colored bar ──────────────────────────
const getBarColor = (position) => {
  if (position <= 4) return { borderLeft: '4px solid #2563eb' }; // Champions League - blue
  if (position === 5) return { borderLeft: '4px solid #f97316' }; // Europa League - orange
  if (position >= 18) return { borderLeft: '4px solid #dc2626' }; // Relegation - red
  return null; // No bar
};

// ─── Shared table row (live + predicted) ───────────────────────────────
const TeamNameCell = ({ teamName, logoDir, crestMap, crestUrl }) => (
  <div className="flex items-center gap-3 min-w-0">
    <TeamLogo
      teamName={teamName}
      logoDir={logoDir}
      crestMap={{
        ...crestMap,
        ...(crestUrl ? { [normalizeCrestKey(teamName)]: crestUrl } : {}),
      }}
    />
    <span className="truncate font-semibold text-gray-900">{teamName}</span>
  </div>
);

const LeagueTableShell = ({ children, extraHeader = null }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm text-gray-900">
      <thead className="text-gray-600 uppercase text-xs border-b-2 border-gray-300 bg-gray-50">
        <tr>
          <th className="py-3 px-2 text-center w-12 font-semibold">Pos</th>
          <th className="py-3 px-2 text-left font-semibold">Team</th>
          {extraHeader}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

// ─── Live / frozen league table ─────────────────────────────────────────
const LiveLeagueTable = ({ competition, season, logoDir, title, crestMap }) => {
  const [liveTable, setLiveTable] = useState([]);
  const [meta, setMeta] = useState({ frozen: false, frozenAt: null, missingSnapshot: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    API.get("/table-predictions/standings", { params: { competition, season } })
      .then(({ data }) => {
        setLiveTable(Array.isArray(data?.rows) ? data.rows : []);
        setMeta({
          frozen: Boolean(data?.frozen),
          frozenAt: data?.frozenAt || null,
          missingSnapshot: Boolean(data?.missingSnapshot),
        });
      })
      .catch(() => setError("⚠️ No league table data available."))
      .finally(() => setLoading(false));
  }, [competition, season]);

  const tableTitle = meta.frozen
    ? `Final ${formatSeasonLabel(season)} ${title}`
    : `Live ${title}`;

  return (
    <Card title={tableTitle} className="h-full">
      {meta.frozen && meta.frozenAt && (
        <p className="text-xs text-gray-500 mb-3">
          Final standings frozen — scores for {formatSeasonLabel(season)} will not change.
        </p>
      )}
      {meta.missingSnapshot && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
          Final {formatSeasonLabel(season)} standings are not frozen yet. Scores may be unavailable until an admin freezes them.
        </p>
      )}
      {loading ? (
        <LoadingSpinner message="Loading table..." />
      ) : error ? (
        <p className="text-red-600 text-center">{error}</p>
      ) : (
        <LeagueTableShell extraHeader={<th className="py-3 px-2 text-right w-16 font-semibold">Pts</th>}>
          {liveTable.map((team, idx) => (
            <tr
              key={idx}
              className="border-b border-gray-100"
              style={getBarColor(team.position) || {}}
            >
              <td className="py-3 px-2 text-center font-mono text-gray-700 w-12">
                {team.position}
              </td>
              <td className="py-3 px-2">
                <TeamNameCell
                  teamName={team.team_name}
                  logoDir={logoDir}
                  crestMap={crestMap}
                  crestUrl={team.crest_url}
                />
              </td>
              <td className="py-3 px-2 text-right font-mono text-gray-700">
                {team.points}
              </td>
            </tr>
          ))}
        </LeagueTableShell>
      )}
    </Card>
  );
};

// ─── Sortable table row (desktop drag) ─────────────────────────────────
const SortableTableRow = ({ id, team, logoDir, crestMap }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(getBarColor(team.position) || {}),
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="border-b border-gray-100 cursor-grab bg-white hover:bg-gray-50"
    >
      <td className="py-3 px-2 text-center font-mono text-gray-700 w-12">
        {team.position}
      </td>
      <td className="py-3 px-2">
        <TeamNameCell teamName={team.team_name} logoDir={logoDir} crestMap={crestMap} />
      </td>
    </tr>
  );
};

const PredictedTableRow = ({
  team,
  logoDir,
  crestMap,
  locked,
  teamPoints,
  deltas,
  mobileReorder,
  draggable,
  onMoveUp,
  onMoveDown,
  predictionsLength,
}) => {
  const delta = deltas?.[team.team_name];
  const points = locked ? teamPoints?.[team.team_name] ?? 0 : null;
  const deltaBadge =
    delta === undefined ? null : (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
          delta === 0
            ? "bg-gray-100 text-gray-700"
            : delta > 0
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700"
        }`}
      >
        {delta > 0 ? `▲ ${delta}` : delta < 0 ? `▼ ${Math.abs(delta)}` : "="}
      </span>
    );

  return (
    <tr className="border-b border-gray-100 bg-white" style={getBarColor(team.position) || {}}>
      <td className="py-3 px-2 text-center font-mono text-gray-700 w-12">
        {team.position}
      </td>
      <td className="py-3 px-2">
        <TeamNameCell teamName={team.team_name} logoDir={logoDir} crestMap={crestMap} />
      </td>
      {(locked || mobileReorder) && (
        <td className="py-3 px-2 text-right whitespace-nowrap">
          <div className="flex items-center justify-end gap-2">
            {points !== null && (
              <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                {points} pts
              </span>
            )}
            {deltaBadge}
            {mobileReorder && draggable && (
              <div className="flex items-center gap-1 ml-1">
                <button
                  type="button"
                  onClick={() => onMoveUp?.(team.position)}
                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 disabled:opacity-40"
                  disabled={team.position === 1}
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={() => onMoveDown?.(team.position)}
                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 disabled:opacity-40"
                  disabled={team.position === predictionsLength}
                >
                  Down
                </button>
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

// ─── Display predictions (same table UI as live standings) ───────────────
const PredictedTableDisplay = ({
  predictions,
  draggable,
  logoDir,
  crestMap = {},
  deltas,
  teamPoints,
  locked,
  mobileReorder = false,
  onMoveUp,
  onMoveDown,
}) => {
  const showExtraCol = locked || mobileReorder;

  if (draggable && !mobileReorder) {
    return (
      <LeagueTableShell>
        {predictions.map((team, idx) => (
          <SortableTableRow
            key={`${team.team_name}-${idx}`}
            id={team.position}
            team={team}
            logoDir={logoDir}
            crestMap={crestMap}
          />
        ))}
      </LeagueTableShell>
    );
  }

  return (
    <LeagueTableShell
      extraHeader={
        showExtraCol ? (
          <th className="py-3 px-2 text-right font-semibold">Score</th>
        ) : null
      }
    >
      {predictions.map((team, idx) => (
        <PredictedTableRow
          key={`${team.team_name}-${idx}`}
          team={team}
          logoDir={logoDir}
          crestMap={crestMap}
          locked={locked}
          teamPoints={teamPoints}
          deltas={deltas}
          mobileReorder={mobileReorder}
          draggable={draggable}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          predictionsLength={predictions.length}
        />
      ))}
    </LeagueTableShell>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────
export default function TablePredictionsPage() {
  usePageTitle('Table Predictions');
  const { user, isAdmin } = useContext(UserContext);

  const enabledIds = useMemo(
    () => getEnabledCompetitionIds(user),
    [user?.enabled_competitions, user?.id]
  );
  const visibleCompetitions = useMemo(
    () => filterCompetitionsByEnabled(TABLE_COMPETITIONS, enabledIds),
    [enabledIds]
  );

  const [competition, setCompetition] = useState(() =>
    pickInitialCompetitionId(
      enabledIds.filter((id) => TABLE_COMPETITIONS.some((c) => c.id === id)),
      "premier_league"
    )
  );
  const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
  const [seasonOptions, setSeasonOptions] = useState(SEASON_OPTIONS);
  const [predictedTable, setPredictedTable] = useState([]);
  const [locked, setLocked] = useState(false);
  const [pastDeadline, setPastDeadline] = useState(false);
  const [tableDeadline, setTableDeadline] = useState(null);
  const [loadingPreds, setLoadingPreds] = useState(true);
  const [loadingLock, setLoadingLock] = useState(true);
  const [deltas, setDeltas] = useState({});
  const [teamPoints, setTeamPoints] = useState({});
  const [scoreCard, setScoreCard] = useState({ totalPoints: 0, rank: null, perfectPicks: 0, avgDelta: null });
  const [standingsNotice, setStandingsNotice] = useState("");
  const [standingsSeasonMismatch, setStandingsSeasonMismatch] = useState(false);
  const [liveStandingsSeason, setLiveStandingsSeason] = useState(null);
  const [crestMap, setCrestMap] = useState({});
  const [toast, setToast] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const token = localStorage.getItem("token");
  const showToast = (type, message, duration = 2500) => {
    setToast({ type, message });
    if (duration > 0) {
      setTimeout(() => setToast(null), duration);
    }
  };

  const logoDir = useMemo(() => {
    const found = TABLE_COMPETITIONS.find((c) => c.id === competition);
    return found?.logoDir || "england__premier_league";
  }, [competition]);
  const compLabel = useMemo(
    () => TABLE_COMPETITIONS.find((c) => c.id === competition)?.label || "Premier League",
    [competition]
  );
  const seasonItems = useMemo(
    () =>
      sortSeasonIdsDesc(seasonOptions.map((s) => s.id)).map((id) => {
        const found = seasonOptions.find((s) => s.id === id);
        return { key: id, label: found?.label || formatSeasonLabel(id) || id };
      }),
    [seasonOptions]
  );
  const isPastSeason = selectedSeason !== CURRENT_SEASON;
  const effectivelyLocked = locked || pastDeadline || isPastSeason;
  const showOpenLeaguesBanner =
    !isPastSeason &&
    shouldShowTablePredictionsOpenBanner() &&
    visibleCompetitions.length > 0;

  const tableDeadlineLabel = useMemo(() => {
    if (!tableDeadline) return "";
    const d = new Date(tableDeadline);
    if (Number.isNaN(d.getTime())) return "";
    if (d.getTime() <= Date.now()) return "Predictions closed";
    const diffMs = d.getTime() - Date.now();
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remH = hours % 24;
      return remH > 0 ? `Closes in ${days}d ${remH}h` : `Closes in ${days}d`;
    }
    if (hours > 0) return mins > 0 ? `Closes in ${hours}h ${mins}m` : `Closes in ${hours}h`;
    return `Closes in ${mins}m`;
  }, [tableDeadline]);

  useEffect(() => {
    API.get("/leaderboard/seasons", { params: { competition } })
      .then(({ data }) => {
        const fromApi = Array.isArray(data?.seasons) ? data.seasons : [];
        const merged = sortSeasonIdsDesc([
          ...new Set([...fromApi, ...SEASON_OPTIONS.map((s) => s.id)]),
        ]);
        setSeasonOptions(
          merged.map((id) => ({
            id,
            label: formatSeasonLabel(id) || id,
          }))
        );
        setSelectedSeason((prev) =>
          merged.includes(prev) ? prev : merged[0] || CURRENT_SEASON
        );
      })
      .catch(() => setSeasonOptions(SEASON_OPTIONS));
  }, [competition]);

  useEffect(() => {
    const allowed = enabledIds.filter((id) => TABLE_COMPETITIONS.some((c) => c.id === id));
    if (!allowed.includes(competition)) {
      setCompetition(pickInitialCompetitionId(allowed, "premier_league"));
    }
  }, [enabledIds, competition]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // fetch predictions + lock status (breakdown loads separately so UI is not blocked)
  useEffect(() => {
    let cancelled = false;
    setLoadingPreds(true);
    setLoadingLock(true);
    const seasonParams = { competition, season: selectedSeason };
    const pastSeason = selectedSeason !== CURRENT_SEASON;

    const applyStandingsAndPredictions = (standingsRes, predsRes, lockRes) => {
      const standingsRows = Array.isArray(standingsRes.data?.rows)
        ? standingsRes.data.rows
        : [];
      const map = {};
      standingsRows.forEach((row) => {
        addCrestToMap(map, row.team_name, row.crest_url);
      });
      setCrestMap(map);
      setStandingsSeasonMismatch(Boolean(standingsRes.data?.seasonMismatch));
      setLiveStandingsSeason(standingsRes.data?.liveStandingsSeason || null);

      setPredictedTable(
        resolvePredictedTable({
          savedPredictions: predsRes.data,
          standingsRows,
          competition,
          isPast: pastSeason,
        })
      );

      setLocked(!!lockRes.data?.locked);
      setPastDeadline(!!lockRes.data?.pastDeadline);
      setTableDeadline(lockRes.data?.deadline || null);
    };

    Promise.all([
      API.get("/table-predictions/standings", { params: seasonParams }),
      API.get("/table-predictions/my-predictions", { params: seasonParams }),
      API.get("/table-predictions/is-locked", { params: { competition } }),
    ])
      .then(([standingsRes, predsRes, lockRes]) => {
        if (cancelled) return;
        applyStandingsAndPredictions(standingsRes, predsRes, lockRes);
      })
      .catch(() => {
        if (cancelled) return;
        setCrestMap({});
        setStandingsSeasonMismatch(false);
        setLiveStandingsSeason(null);
        setPredictedTable(pastSeason ? [] : buildDefaultTable(competition));
        setLocked(false);
        setPastDeadline(false);
        setTableDeadline(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingPreds(false);
        setLoadingLock(false);
      });

    API.get("/table-predictions/my-breakdown", { params: seasonParams })
      .then(({ data: breakdownRes }) => {
        if (cancelled) return;
        const d = {};
        const tp = {};
        breakdownRes?.breakdown?.forEach((row) => {
          if (row.delta !== null && row.delta !== undefined) d[row.team_name] = row.delta * -1;
          tp[row.team_name] = row.points || 0;
        });
        setDeltas(d);
        setTeamPoints(tp);
        setScoreCard({
          totalPoints: breakdownRes?.totalPoints || 0,
          rank: breakdownRes?.rank ?? null,
          perfectPicks: breakdownRes?.perfectPicks || 0,
          avgDelta: breakdownRes?.avgDelta,
        });
        setStandingsNotice(breakdownRes?.message || "");
      })
      .catch(() => {
        if (cancelled) return;
        setDeltas({});
        setTeamPoints({});
        setScoreCard({ totalPoints: 0, rank: null, perfectPicks: 0, avgDelta: null });
        setStandingsNotice("");
      });

    return () => {
      cancelled = true;
    };
  }, [competition, selectedSeason, token]);

  const handleLock = (lock) => {
    API.put("/table-predictions/toggle-lock", { lock, competition })
      .then(() => {
        setLocked(lock);
        showToast("success", lock ? "Table predictions locked." : "Table predictions unlocked.");
      })
      .catch((e) => showToast("error", e.response?.data?.message || e.message));
  };

  const handleSyncStandings = () => {
    showToast("info", "Syncing standings...");
    API.post(`/admin/sync-standings?competition=${competition}`)
      .then(() => showToast("success", "Standings synced"))
      .catch((e) => showToast("error", e.response?.data?.message || e.message));
  };

  // handle drag & reorder
  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const oldIndex = predictedTable.findIndex((t) => t.position === active.id);
    const newIndex = predictedTable.findIndex((t) => t.position === over.id);
    const updated = arrayMove(predictedTable, oldIndex, newIndex).map((t, i) => ({
      ...t,
      position: i + 1,
    }));
    setPredictedTable(updated);
  };

  const moveByPosition = (position, direction) => {
    const oldIndex = predictedTable.findIndex((t) => t.position === position);
    if (oldIndex < 0) return;
    const newIndex = oldIndex + direction;
    if (newIndex < 0 || newIndex >= predictedTable.length) return;
    const updated = arrayMove(predictedTable, oldIndex, newIndex).map((t, i) => ({
      ...t,
      position: i + 1,
    }));
    setPredictedTable(updated);
  };

  // submit to server
  const handleSubmit = () => {
    if (effectivelyLocked) {
      showToast(
        "error",
        isPastSeason
          ? "Past season predictions are read-only."
          : pastDeadline
          ? "Table prediction deadline has passed."
          : "Table predictions are locked."
      );
      return;
    }
    API.post("/table-predictions/submit", {
      competition,
      season: selectedSeason,
      predictions: predictedTable.map(({ position, team_name }) => ({ position, team_name })),
    })
      .then(() => {
        showToast("success", "Table predictions submitted.");
        return API.get("/table-predictions/my-predictions", {
          params: { competition, season: selectedSeason },
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then(({ data }) => {
        setPredictedTable((prev) => {
          if (Array.isArray(data) && data.length > 0) return data;
          return prev;
        });
      })
      .catch((e) => showToast("error", `Submission failed: ${e.response?.data?.message || e.message}`));
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      {toast && (
        <div
          className={`fixed bottom-20 md:bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-semibold text-white ${
            toast.type === "success"
              ? "bg-green-600"
              : toast.type === "error"
              ? "bg-red-600"
              : "bg-gray-700"
          }`}
        >
          {toast.message}
        </div>
      )}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-[22px] md:text-[28px] font-bold text-ink">Table</h1>
            <p className="mt-1 text-meta text-muted">
              Predict the final league table for {formatSeasonLabel(selectedSeason)}.
            </p>
          </div>
          {seasonItems.length > 0 && (
            <SeasonSelect
              seasons={seasonItems}
              value={selectedSeason}
              onChange={setSelectedSeason}
            />
          )}
        </div>

        <CompetitionFilterBar
          items={visibleCompetitions.map((c) => ({ key: c.id, label: c.label }))}
          activeKey={competition}
          onChange={setCompetition}
        />

        {showOpenLeaguesBanner && (
          <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <span
                  className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                  aria-hidden
                />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {formatSeasonLabel(selectedSeason)}
                  </p>
                  <p className="text-sm text-gray-800 mt-0.5">
                    Table predictions are open
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {visibleCompetitions.map((c) => (
                  <span
                    key={c.id}
                    className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-800"
                  >
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {isPastSeason && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            Viewing <strong>{formatSeasonLabel(selectedSeason)}</strong> — predictions and scores are locked to the
            frozen final table from that season.
            {standingsNotice ? ` ${standingsNotice}` : ""}
          </div>
        )}

        {!isPastSeason && standingsSeasonMismatch && !loadingPreds && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Live standings are still tagged as{" "}
            <strong>{formatSeasonLabel(liveStandingsSeason || "2025-26")}</strong>. Run{" "}
            <strong>Sync standings</strong> in Admin for {compLabel} so table predictions use{" "}
            {formatSeasonLabel(selectedSeason)} teams.
          </div>
        )}

        {!isPastSeason && tableDeadline && !loadingLock && (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              pastDeadline
                ? "border-gray-200 bg-gray-100 text-gray-700"
                : "border-blue-200 bg-blue-50 text-blue-900"
            }`}
          >
            {pastDeadline ? (
              <>Table predictions closed on {new Date(tableDeadline).toLocaleString()}.</>
            ) : (
              <>
                Table predictions close on{" "}
                <strong>{new Date(tableDeadline).toLocaleString()}</strong>
                {tableDeadlineLabel ? ` · ${tableDeadlineLabel}` : ""}
              </>
            )}
          </div>
        )}

        {!isPastSeason && locked && !pastDeadline && !loadingLock && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Table predictions are manually locked by an admin.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* Score card combined */}
          <div className="md:col-span-2">
            <Card title="Your Table Score">
              <div className="grid sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Points</p>
                  <p className="text-[22px] font-bold text-ink">{scoreCard.totalPoints ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Rank</p>
                  <p className="text-[22px] font-bold text-ink">{scoreCard.rank ?? "-"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Perfect Picks</p>
                  <p className="text-[22px] font-bold text-ink">{scoreCard.perfectPicks ?? 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Avg Delta</p>
                  <p className="text-[22px] font-bold text-ink">{scoreCard.avgDelta ?? "-"}</p>
                </div>
              </div>
            </Card>
            {/* Admin controls */}
            {isAdmin && (
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={() => handleLock(true)}
                className="px-3 py-2 rounded-md border bg-red-50 text-red-700 text-sm font-semibold"
              >
                Lock
              </button>
              <button
                onClick={() => handleLock(false)}
                className="px-3 py-2 rounded-md border bg-green-50 text-green-700 text-sm font-semibold"
              >
                Unlock
              </button>
              <button
                onClick={handleSyncStandings}
                className="px-3 py-2 rounded-md border bg-blue-50 text-blue-700 text-sm font-semibold"
              >
                Sync standings
              </button>
            </div>
            )}
          </div>
          {/* Live Table */}
          <LiveLeagueTable
            competition={competition}
            season={selectedSeason}
            logoDir={logoDir}
            title={compLabel}
            crestMap={crestMap}
          />

          {/* Your Predictions */}
          <Card
            title={`Your Predicted Table — ${formatSeasonLabel(selectedSeason)}`}
            action={
              !loadingPreds && !loadingLock && effectivelyLocked ? (
                <span className="inline-block bg-red-50 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium border border-red-200">
                  {isPastSeason ? "📅 Past season" : "🚫 Locked"}
                </span>
              ) : null
            }
            className="h-full"
          >
            {loadingPreds || loadingLock ? (
              <LoadingSpinner message="Loading predictions..." />
            ) : effectivelyLocked ? (
              <PredictedTableDisplay
                predictions={predictedTable}
                logoDir={logoDir}
                crestMap={crestMap}
                deltas={deltas}
                teamPoints={teamPoints}
                locked
              />
            ) : (
              <>
                {isMobile ? (
                  <PredictedTableDisplay
                    predictions={predictedTable}
                    draggable
                    mobileReorder
                    logoDir={logoDir}
                    crestMap={crestMap}
                    teamPoints={teamPoints}
                    deltas={deltas}
                    onMoveUp={(pos) => moveByPosition(pos, -1)}
                    onMoveDown={(pos) => moveByPosition(pos, 1)}
                  />
                ) : (
                  <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={predictedTable.map((t) => t.position)}>
                      <PredictedTableDisplay
                        predictions={predictedTable}
                        draggable
                        logoDir={logoDir}
                        crestMap={crestMap}
                        teamPoints={teamPoints}
                        deltas={deltas}
                      />
                    </SortableContext>
                  </DndContext>
                )}
                <button
                  onClick={handleSubmit}
                  className="mt-6 w-full h-11 px-6 bg-brand text-white rounded-lg font-semibold hover:bg-brand-hover"
                >
                  Submit Table Predictions
                </button>
              </>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
